import type { ComponentProps, PropsWithChildren, ReactNode } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type {
  AlignItemsProps,
  BorderRadiusProps,
  BoxShadowProps,
  DisplayProps,
  FlexDirectionProps,
  FlexWrapProps,
  JustifyContentProps,
  MaxWidthProps,
  MinWidthProps,
  OrderProps,
  PositionProps,
  ResponsiveValue,
  SpaceProps,
  TopProps,
  WidthProps,
  ZIndexProps,
} from 'styled-system';
import {
  alignItems,
  backgroundColor,
  borderRadius,
  boxShadow,
  color,
  flexDirection,
  flexWrap,
  justifyContent,
  maxWidth,
  minWidth,
  order,
  position,
  space,
  display as styledDisplay,
  width as styledWidth,
  top,
  zIndex,
} from 'styled-system';

import { GRID_MAX_WIDTH } from '../constants/grid';
import type { BackgroundColorProps, ColorProps } from '../theme/system-types';
import {
  flexGrow,
  overflowX,
  type AsProps,
  type FlexGrowProps,
  type OverflowXProps,
} from '../theme/system-utilities';
import { Flex } from './Flex';

const UnstyledGridWrapper = styled(Flex)<MinWidthProps & BoxShadowProps & BackgroundColorProps>`
  ${backgroundColor};
  ${minWidth};
  ${maxWidth};
  ${boxShadow};
`;

const GridWrapper = (props: ComponentProps<typeof UnstyledGridWrapper>) => (
  <UnstyledGridWrapper
    width={1}
    backgroundColor="sys.neutral.background.default"
    minWidth={320}
    maxWidth="100%"
    {...props}
  />
);

export enum FullWidthBoxShadow {
  BOTTOM = 'bottom',
}

export const enum FlexJustifyContent {
  CENTER = 'center',
  SPACE_BETWEEN = 'space-between',
}

export enum FlexAlignItems {
  CENTER = 'center',
  START = 'flex-start',
}

export enum FlexWrap {
  WRAP = 'wrap',
  NO_WRAP = 'nowrap',
}

// S (320-631):
// - total area (content area + offset): 320px
// - content area: 288px
// - offset: 16px
// - gutter: 16px
// - column: 60px
// - 4 columns

// M (632-983):
// - total area (content area + offset): 632px
// - content area: 584px
// - offset: 24px
// - gutter: 24px
// - column: 52px
// - 8 columns

// L (984-1391):
// - total area (content area + offset): 984px
// - content area: 888px
// - offset: 48px
// - gutter: 24px
// - column: 52px
// - 12 columns

// XL (1392-1600):
// - total area (content area + offset): 1392px
// - content area: 1264px
// - offset: 64px
// - gutter: 32px
// - column: 76px
// - 12 columns

const UnstyledGridContainer = styled.div<
  FlexWrapProps & AlignItemsProps & JustifyContentProps & MaxWidthProps & SpaceProps
>`
  width: 100%;
  box-sizing: border-box;

  display: flex;
  ${flexWrap};

  ${alignItems};
  ${justifyContent};

  ${maxWidth};
  ${space};
`;

const GridContainer = (props: ComponentProps<typeof UnstyledGridContainer>) => (
  <UnstyledGridContainer
    mx="auto"
    px={[2, '12px', '36px', 6]}
    maxWidth={GRID_MAX_WIDTH}
    flexWrap="wrap"
    {...props}
  />
);

export interface GridProps extends SpaceProps, AsProps, BackgroundColorProps {
  /** Grid content */
  children?: ReactNode;
  justifyContent?: ResponsiveValue<FlexJustifyContent>;
  alignItems?: ResponsiveValue<FlexAlignItems | string>;
  /** Box shadow that will be applied to the fill width of the container */
  fullWidthBoxShadow?: ResponsiveValue<FullWidthBoxShadow>;
  flexDirection?: ResponsiveValue<string>;
  flexWrap?: ResponsiveValue<FlexWrap>;
  boxShadow?: ResponsiveValue<string>;
  display?: ResponsiveValue<string>;
  /** Grid wrapper min width */
  minWidth?: ResponsiveValue<string>;
  'data-corgi-id'?: string;
  'aria-label'?: string;
  ref?: React.Ref<HTMLDivElement>;
  id?: string;
}

export const Grid = ({
  bg,
  minWidth: minWidthValue,
  children,
  fullWidthBoxShadow,
  as,
  'data-corgi-id': dataCorgiId,
  'aria-label': ariaLabel,
  ref,
  id,
  ...props
}: GridProps) => (
  <GridWrapper
    ref={ref}
    as={as}
    minWidth={minWidthValue}
    boxShadow={fullWidthBoxShadow}
    data-corgi-id={dataCorgiId}
    data-testid="grid-wrapper"
    aria-label={ariaLabel}
    {...(bg !== undefined ? { backgroundColor: bg } : {})}
  >
    <GridContainer id={id} {...props}>
      {children}
    </GridContainer>
  </GridWrapper>
);

Grid.displayName = 'Grid';

const UnstyledGridItemContainer = styled.div<
  SpaceProps &
    WidthProps &
    OrderProps &
    DisplayProps &
    MaxWidthProps &
    FlexDirectionProps &
    OverflowXProps &
    PositionProps &
    FlexGrowProps &
    AlignItemsProps &
    TopProps &
    ZIndexProps &
    BackgroundColorProps &
    BoxShadowProps &
    JustifyContentProps
>`
  display: block;
  flex: none;
  box-sizing: border-box;

  ${space};
  ${styledWidth};
  ${order};
  ${maxWidth}
  ${flexDirection}
  ${styledDisplay}
  ${overflowX}
  ${position}
  ${flexGrow}
  ${alignItems}
  ${top}
  ${zIndex}
  ${backgroundColor}
  ${boxShadow}
  ${justifyContent}
  ${borderRadius}
`;

const GridItemContainer = (props: ComponentProps<typeof UnstyledGridItemContainer>) => (
  <UnstyledGridItemContainer width={1} px={[2, '12px', null, 3]} {...props} />
);

interface GridItemStyledProps {
  w?: WidthProps['width'];
  order?: ResponsiveValue<number>;
}

export type GridItemProps = AsProps &
  WidthProps &
  MaxWidthProps &
  SpaceProps &
  FlexDirectionProps &
  DisplayProps &
  OverflowXProps &
  PositionProps &
  FlexGrowProps &
  AlignItemsProps &
  TopProps &
  ZIndexProps &
  BackgroundColorProps &
  BoxShadowProps &
  JustifyContentProps &
  GridItemStyledProps &
  PropsWithChildren &
  BorderRadiusProps &
  ComponentProps<'div'> & {
    ref?: React.Ref<HTMLDivElement>;
  };

export const GridItem = ({ w = 1, width, ref, ...props }: GridItemProps) => (
  <GridItemContainer width={width || w} {...props} ref={ref} />
);

GridItem.displayName = 'GridItem';

const UnstyledBorder = styled.div<DisplayProps & ColorProps>`
  width: 100%;
  height: 1px;
  margin-top: -1px;

  ${styledDisplay};
  ${color};
`;

const Border = (props: ComponentProps<typeof UnstyledBorder>) => (
  <UnstyledBorder backgroundColor="sys.neutral.background.active" display="block" {...props} />
);

export interface GridItemBorderProps extends WidthProps, DisplayProps {
  w?: WidthProps['width'];
}

/**
 * Creates a zero-height border in the grid where the border width is
 * matching the grid content width
 */
export const GridItemBorder = ({ w, width, display }: GridItemBorderProps) => (
  <GridItem w={w} width={width}>
    <Border display={display} />
  </GridItem>
);
